// @ts-ignore
import XlsxPopulate from 'xlsx-populate/browser/xlsx-populate';
import { TotalRow } from './rollup';
import { NaverMonthRow, CoupangMonthRow, EasyCheckMonthRow, HometaxMonthRow, TumblbugMonthRow } from './types';

export interface VatExcelInput {
  total: TotalRow[];
  naver: { channel: string; rows: NaverMonthRow[] }[];
  coupang: CoupangMonthRow[];
  easyCheck: EasyCheckMonthRow[];
  hometax: HometaxMonthRow[];
  tumblbug: TumblbugMonthRow[];
}

function writeGrid(ws: any, header: string[], rows: (string | number)[][], totalRow?: (string | number)[]) {
  header.forEach((h, c) => ws.cell(1, c + 1).value(h));
  rows.forEach((row, r) => row.forEach((v, c) => ws.cell(r + 2, c + 1).value(v)));
  if (totalRow) totalRow.forEach((v, c) => ws.cell(rows.length + 2, c + 1).value(v));
}

function sum(rows: any[], pick: (r: any) => number): number {
  return rows.reduce((a, r) => a + pick(r), 0);
}

export async function buildVatWorkbook(input: VatExcelInput): Promise<any> {
  const wb = await XlsxPopulate.fromBlankAsync();
  const first = wb.sheet(0);
  first.name('총 합계');

  // 총 합계
  writeGrid(
    first,
    ['기간', '합계', '과세매출금액', '면세매출금액'],
    input.total.map(t => [t.month, t.total, t.taxable, t.exempt]),
    ['총 합계', sum(input.total, t => t.total), sum(input.total, t => t.taxable), sum(input.total, t => t.exempt)],
  );

  // 네이버 채널들 (제주/남해/해피빈)
  const naverHeader = [
    '기간', '합계', '과세 금액', '면세 금액',
    '신용카드매출전표_총합', '신용카드매출전표_과세', '신용카드매출전표_면세',
    '현금영수증(소득공제)_총합', '현금영수증(소득공제)_과세', '현금영수증(소득공제)_면세',
    '현금영수증(지출증빙)_총합', '현금영수증(지출증빙)_과세', '현금영수증(지출증빙)_면세',
    '현금영수증(발행제외)_총합', '현금영수증(발행제외)_과세', '현금영수증(발행제외)_면세',
    '기타_총합', '기타_과세', '기타_면세',
  ];
  for (const ch of input.naver) {
    const ws = wb.addSheet(ch.channel);
    const rows = ch.rows.map(r => [
      r.month, r.taxable + r.exempt, r.taxable, r.exempt,
      r.card.total, r.card.taxable, r.card.exempt,
      r.cashIncome.total, r.cashIncome.taxable, r.cashIncome.exempt,
      r.cashExpense.total, r.cashExpense.taxable, r.cashExpense.exempt,
      r.cashExcluded.total, r.cashExcluded.taxable, r.cashExcluded.exempt,
      r.etc.total, r.etc.taxable, r.etc.exempt,
    ]);
    writeGrid(ws, naverHeader, rows);
  }

  // 쿠팡
  const coupang = wb.addSheet('쿠팡');
  writeGrid(coupang,
    ['기간', '합계(면세)', '신용/체크카드 발행 매출', '현금영수증 발행 매출', '기타'],
    input.coupang.map(r => [r.month, r.total, r.card, r.cash, r.etc]),
  );

  // 오프라인(이지체크)
  const easy = wb.addSheet('오프라인(이지체크)');
  writeGrid(easy,
    ['기간', '합계', '과세 금액', '면세금액', '현금영수증_과세', '현금영수증_면세', '신용카드_과세', '신용카드_면세'],
    input.easyCheck.map(r => [
      r.month, r.taxable + r.exempt, r.taxable, r.exempt,
      r.cashReceipt.taxable, r.cashReceipt.exempt, r.card.taxable, r.card.exempt,
    ]),
  );

  // 홈택스
  const hometax = wb.addSheet('홈택스');
  writeGrid(hometax,
    ['기간', '합계', '계산서 매출(면세)', '세금계산서 매출(과세)'],
    input.hometax.map(r => [r.month, r.exempt + r.taxable, r.exempt, r.taxable]),
  );

  // 텀블벅 (전부 면세)
  const tumblbug = wb.addSheet('텀블벅');
  writeGrid(tumblbug,
    ['기간', '합계(면세)'],
    input.tumblbug.map(r => [r.month, r.exempt]),
  );

  return wb;
}

export async function buildVatExcelBlob(input: VatExcelInput): Promise<Blob> {
  const wb = await buildVatWorkbook(input);
  return wb.outputAsync() as Promise<Blob>;
}
